import React from 'react';
import { UseMutationResult } from 'react-query';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { VisualPicker } from '../../../CommonComponents/OPL/VisualPicker/VisualPicker';
import { useAddWidgetModal } from './useAddWidgetModal';
import { FetcherParams, useWidgetsMutation } from '../../api/useWidgetsMutation';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { WidgetDTO, WidgetsDTO } from '../../api/useWidgetsQuery';
import { ICON_MAP } from '../DesktopWidgets/DesktopWidgets.meta';
import { TabSlug } from '../../types';

interface Props {
  tab: TabSlug;
  widgets: WidgetsDTO;
  onClose(): void;
}

export const AddWidgetModal = ({ tab, widgets, onClose }: Props) => {
  const { mutateAsync, error, isLoading } = useWidgetsMutation({ tab });

  const onSave = async (newWidgets: WidgetsDTO, onSuccess = () => {}) => {
    await mutateAsync({ tab, widgets: newWidgets });

    onSuccess();
  };

  return (
    <AddWidgetModalContent
      widgets={widgets}
      onClose={onClose}
      onSave={onSave}
      error={error}
      isLoading={isLoading}
    />
  );
};

interface AddWidgetModalContentProps {
  widgets: WidgetsDTO;
  onClose(): void;
  onSave: (newWidgets: WidgetsDTO, onSuccess?: () => void) => Promise<void> | void;
  error?: UseMutationResult<WidgetsDTO, Error, FetcherParams, unknown>['error'];
  isLoading?: UseMutationResult<WidgetsDTO, Error, FetcherParams, unknown>['isLoading'];
}

export const AddWidgetModalContent = ({
  widgets,
  onClose,
  onSave,
  error,
  isLoading,
}: AddWidgetModalContentProps) => {
  const { fields, setValue, handleSubmit } = useAddWidgetModal({ widgets });

  const onSubmit = handleSubmit(async values => {
    try {
      const selectedWidgets = values.widgets.reduce((acc, widget) => {
        if (widget.isSelected) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { isSelected, ...selectedWidget } = widget;

          acc.push(selectedWidget);
        }

        return acc;
      }, [] as WidgetDTO[]);

      const newWidgets: WidgetsDTO = {
        included: [...widgets.included, ...selectedWidgets],
        excluded: widgets.excluded.filter(
          ({ id }) => !selectedWidgets.find(({ id: selectedWidgetId }) => id === selectedWidgetId),
        ),
      };

      await onSave(newWidgets, () => {
        toast.success(
          `Card${selectedWidgets.length > 1 ? 's' : ''} added to dashboard successfully`,
        );
      });

      onClose();
    } catch (err) {
      console.error(err);
    }
  });

  return (
    <Modal isOpen onRequestClose={onClose} aria={{ labelledby: 'add-widget--modal-header' }}>
      <ModalHeader as="h2" id="add-widget--modal-header">
        Add Card
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody id="add-widget--form" as="form" onSubmit={onSubmit}>
        {error && <Alert message={error.message} variant="error" mb="16" />}

        {fields.map((field, index) => {
          return (
            <VisualPicker
              key={field.key}
              icon={ICON_MAP[field.id]}
              variant="checkbox"
              label={field.title}
              helperText={field.description}
              checked={field.isSelected}
              onChange={evt => setValue(`widgets.${index}.isSelected`, evt.target.checked)}
              mt="8"
            />
          );
        })}
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button ml="8" type="submit" form="add-widget--form" isLoading={isLoading}>
          Add Card
        </Button>
      </ModalFooter>
    </Modal>
  );
};
